import { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { getSubContracts } from "../../api/contract/sub";
import {
  getSubStatusStatistics,
  getContractCounts,
} from "../../api/contract/statistics";
import BoardTable from "../../components/common/board/BoardTable";
import BoardPagination from "../../components/common/board/BoardPagination";
import SearchBar from "../../components/common/board/SearchFilterBar";
import Sub_view from "./Sub_view";
import PageHeader from "../../components/common/ui/PageHeader";

const STATUS_LABELS = {
  DRAFT: "Draft (임시)",
  FOR_INFORMATION: "For Information (정보)",
  FOR_REVIEW: "For Review (검토)",
  FOR_APPROVAL: "For Approval (승인)",
  FINAL: "Final (최종)",
  AS_BUILT: "As-Built (준공)",
  NO_LONGER_IN_USE: "No Longer In Use (폐기)",
};

export default function Sub() {
  const { id: projectId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const queryParams = new URLSearchParams(location.search);
  const constructionType = queryParams.get("construction_type") || "";

  const [statusStats, setStatusStats] = useState({});
  //캘린더
  const [calendarData, setCalendarData] = useState([]);
  const [loadingCalendar, setLoadingCalendar] = useState(true);
  const contract_type = "sub";

  // 페이지 관리
  const [q, setQ] = useState("");
  const [searchField, setSearchField] = useState("title"); // 기본 검색 필드
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [searchTrigger, setSearchTrigger] = useState(0);

  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState("");
  const [total, setTotal] = useState(0);
  const [selectedId, setSelectedId] = useState(null);

  // 필터 상태 (상태 버튼 / 분야 select)
  const [status, setStatus] = useState("");
  const [detailType, setDetailType] = useState("");

  const [dateFilter, setDateFilter] = useState("updated_at"); // updated_at 등
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // 검색 실행
  const applySearch = () => {
    setPage(1);
    setSearchTrigger(prev => prev + 1);
  };
  // 계약 데이터 가져오기
  const fetchContracts = async () => {
    setLoading(true);
    try {
      const params = {
        project: projectId,
        construction_type: constructionType,
        [searchField]: q || undefined,

        // 날짜 필터 추가
        date_filter: dateFilter || undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,

        page,
        page_size: pageSize,
      };

      const res = await getSubContracts(projectId, params);
      setContracts(res.results ?? res);
      setTotal(res.count ?? res.total ?? res.length);
    } catch {
      setErrMsg("목록을 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!projectId) return;
    async function fetchStatusStats() {
      try {
        const data = await getSubStatusStatistics(
          projectId,
          constructionType,
          contract_type
        );
        setStatusStats(data);
      } catch (e) {
        console.error("상태 통계 불러오기 실패:", e);
      }
    }
    fetchStatusStats();
  }, [projectId, constructionType]);

  useEffect(() => {
    fetchContracts();
  }, [projectId, constructionType, page, pageSize, searchTrigger]);

  // 검색
  const handleSearch = () => {
    setPage(1); // 검색하면 페이지 초기화
    setSearchTrigger((prev) => prev + 1); // 트리거 증가
  };

  // 엔터 입력 시 검색
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleRowClick = (contractId) => {
    setSelectedId(contractId);
  };

  const handleCreate = () => {
    navigate("new", { state: { construction_type: constructionType } });
  };

  const rows = useMemo(() => contracts ?? [], [contracts]);

  //기간 위험도 측정
  const columns = [
    {
      key: "contract_code",
      header: "코드",
      render: (row) => row.meta.contract_code,
    },
    { key: "title", header: "계약명" },
    {
      key: "construction_type_detail_display",
      header: "세부분야",
      render: (row) => row.meta.construction_type,
    },
    { key: "revision", header: "Rev.No", render: (row) => row.meta.revision },
    { key: "version", header: "Ver.", render: (row) => row.meta.version },
    {
      key: "status_display",
      header: "계약 상태",
      render: (row) => STATUS_LABELS[row.meta.status],
    },
  ];

  return (
    <div className=" mx-auto p-6 rounded-2xl bg-gray-50">
      {/* 헤더 */}
      <PageHeader>하도급계약 관리</PageHeader>

      {/* 상태 필터 박스 */}
      {/* <div className="mb-4 bg-white p-4 rounded-lg shadow-sm">
        <div className="flex flex-wrap gap-2">
          {[
            { key: "", label: `전체 (${statusStats?.전체 ?? 0})` },
            {
              key: "DRAFT",
              label: `Draft(임시) (${statusStats?.status?.DRAFT ?? 0})`,
            },
            {
              key: "FOR_INFORMATION",
              label: `For Information(정보) (${
                statusStats?.status?.FOR_INFORMATION ?? 0
              })`,
            },
            {
              key: "FOR_REVIEW",
              label: `For Review(검토) (${
                statusStats?.status?.FOR_REVIEW ?? 0
              })`,
            },
            {
              key: "FOR_APPROVAL",
              label: `For Approval(승인) (${
                statusStats?.status?.FOR_APPROVAL ?? 0
              })`,
            },
            {
              key: "FINAL",
              label: `Final(최종) (${statusStats?.status?.FINAL ?? 0})`,
            },
            {
              key: "AS_BUILT",
              label: `As-Built(준공) (${statusStats?.status?.AS_BUILT ?? 0})`,
            },
            {
              key: "NO_LONGER_IN_USE",
              label: `No Longer In Use(폐기) (${
                statusStats?.status?.NO_LONGER_IN_USE ?? 0
              })`,
            },
          ].map((opt) => (
            <button
              key={opt.key}
              onClick={() => {
                setStatus(opt.key);
                // 서버 방식이면 검색 트리거로 다시 불러오도록
                handleSearch();
              }}
              className={`px-3 py-1 rounded-md border text-sm ${
                status === opt.key
                  ? "bg-blue-500 text-white border-blue-600"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div> */}

      {/* 검색/분야 필터 박스 */}
      <div className="mb-6">

        <div className="flex items-center justify-between">

          {/* 왼쪽은 완전히 비워둔다 */}
          <div className="flex-1"></div>

          {/* 오른쪽: 검색 + 버튼 */}
          <div className="flex items-center gap-2">

            {/* 검색바 */}
            <SearchBar
              q={q}
              onChangeQ={setQ}
              onSearch={applySearch}
              searchField={searchField}
              onChangeSearchField={setSearchField}
              dateFilter={dateFilter}
              onChangeDateFilter={setDateFilter}
              startDate={startDate}
              endDate={endDate}
              onDateChange={({ start, end }) => {
                setStartDate(start);
                setEndDate(end);
              }}
              pageSize={pageSize}
              onChangePageSize={setPageSize}
            />

            {/* 새 계약 등록 버튼 */}
            <button
              onClick={handleCreate}
              className=" btn-create whitespace-nowrap"
            >
              {constructionType} 새 계약 등록
            </button>

          </div>
        </div>

      </div>

      {/* 리스트 */}
      <BoardTable
        columns={columns}
        rows={rows}
        loading={loading}
        error={errMsg}
        emptyText="계약이 없습니다."
        onRowClick={(row) => handleRowClick(row.dns)}
      />

      {/* 페이지네이션 */}
      <div className="mt-4 mb-2">
        <BoardPagination
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
        />
      </div>

      {/* 선택된 계약 */}
      {selectedId && (
        <div className="mt-4 border-t pt-6">
          <Sub_view
            contractId={selectedId}
            onClose={() => setSelectedId(null)}
            onUpdated={(newId) => {
              setSelectedId(newId);
              fetchContracts();
            }}
            onDeleted={() => { 
              setSelectedId(null);
              fetchContracts();
            }}
          />
        </div>
      )}
    </div>
  );
}
